import type { QueryClient } from "@tanstack/react-query";
import { setSentryUser } from "../sentry";
import type { AuthState } from "./AuthProvider";

// Set just before a forced sign-out on a dead session, so /login can render the
// session-expired copy (spec §6) once it lands. A plain module flag is enough here —
// apiFetch and LoginScreen always run in the same tab/session, no reload in between.
let sessionExpired = false;

export function markSessionExpired(): void {
  sessionExpired = true;
}

// Reads the flag AND resets it, so the banner shows exactly once per expiry, not on
// every subsequent mount of /login (e.g. after a fresh, unrelated sign-in later).
export function consumeSessionExpired(): boolean {
  const was = sessionExpired;
  sessionExpired = false;
  return was;
}

// Centralizes sign-out hygiene: Firebase sign-out + clearing the pseudonymous Sentry
// user, and — only where a QueryClient is available — wiping the query cache so no
// other account's data survives on a shared device (spec §4).
//
// `queryClient` is optional: api.ts's double-401 path (the other caller) has no
// QueryClient reference to hand — it's a plain fetch wrapper, not a query/mutation
// context. Rather than thread one through via a module-singleton "register the
// queryClient" indirection (more magic, harder to reason about who set it and when),
// callers that DO have one (AuthedShell) pass it; the one that doesn't gets sign-out +
// Sentry hygiene, which is the part that matters most on an already-401'd session (its
// cache entries are about to be superseded by the /login redirect and next sign-in
// anyway).
export async function performSignOut(auth: AuthState, queryClient?: QueryClient): Promise<void> {
  await auth.signOut();
  queryClient?.clear();
  setSentryUser(null); // clear the pseudonymous id — events after this must not carry it
}
